import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import Toast from '../ui/Toast';

const NovoLeiloeiroModal = ({ isOpen, onClose, onSave, loading = false }) => {
  const { toasts, error, hideToast } = useToast();
  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    telefone: '',
    email: '',
    perc_venda: '',
    perc_defesa: '',
    ativo: true,
    is_padrao: false
  });
  const [errors, setErrors] = useState({});

  const formatCPF = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  const formatTelefone = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4,5})(\d{4})/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  };

  const validateCPF = (cpf) => {
    if (!cpf) return true; // CPF é opcional
    const cleanCPF = cpf.replace(/\D/g, '');
    return cleanCPF.length === 11;
  };

  const validateTelefone = (telefone) => {
    if (!telefone) return true; // Telefone é opcional
    const cleanTel = telefone.replace(/\D/g, '');
    return cleanTel.length >= 10;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    
    // Remover validação obrigatória do % Defesa
    
    if (formData.cpf && !validateCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido';
    }
    
    if (formData.telefone && !validateTelefone(formData.telefone)) {
      newErrors.telefone = 'Telefone inválido';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = Object.values(newErrors)[0];
      error(firstError);
      return;
    }
    
    const data = {
      nome: formData.nome,
      cpf: formData.cpf || null,
      telefone: formData.telefone || null,
      email: formData.email || null,
      perc_venda: parseFloat(formData.perc_venda) || 0,
      perc_defesa: parseFloat(formData.perc_defesa) || 0,
      ativo: formData.ativo,
      is_padrao: formData.is_padrao
    };
    
    await onSave(data);
    
    setFormData({
      nome: '',
      cpf: '',
      telefone: '',
      email: '',
      perc_venda: '',
      perc_defesa: '',
      ativo: true,
      is_padrao: false
    });
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <>
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => hideToast(toast.id)}
        />
      ))}
      
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Novo Leiloeiro</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome *
            </label>
            <input
              type="text"
              required
              value={formData.nome}
              onChange={(e) => setFormData({...formData, nome: e.target.value})}
              className="input-rematei"
              placeholder="Ex: João Silva"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CPF
              </label>
              <input
                type="text"
                value={formData.cpf}
                onChange={(e) => {
                  const formatted = formatCPF(e.target.value);
                  setFormData({...formData, cpf: formatted});
                  if (errors.cpf) setErrors({...errors, cpf: ''});
                }}
                className={`input-rematei ${errors.cpf ? 'border-red-500' : ''}`}
                placeholder="000.000.000-00"
                maxLength="14"
              />
              {errors.cpf && <p className="text-red-500 text-xs mt-1">{errors.cpf}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefone
              </label>
              <input
                type="text"
                value={formData.telefone}
                onChange={(e) => {
                  const formatted = formatTelefone(e.target.value);
                  setFormData({...formData, telefone: formatted});
                  if (errors.telefone) setErrors({...errors, telefone: ''});
                }}
                className={`input-rematei ${errors.telefone ? 'border-red-500' : ''}`}
                placeholder="(11) 99999-9999"
                maxLength="15"
              />
              {errors.telefone && <p className="text-red-500 text-xs mt-1">{errors.telefone}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="input-rematei"
              placeholder="joao@email.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                % Venda
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.perc_venda}
                onChange={(e) => setFormData({...formData, perc_venda: e.target.value})}
                className="input-rematei"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                % Defesa
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.perc_defesa}
                onChange={(e) => setFormData({...formData, perc_defesa: e.target.value})}
                className="input-rematei"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.ativo}
                onChange={(e) => setFormData({...formData, ativo: e.target.checked})}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">Ativo</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_padrao}
                onChange={(e) => setFormData({...formData, is_padrao: e.target.checked})}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">⭐ Leiloeiro padrão</span>
            </label>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvar
            </button>
          </div>
        </form>
      </div>
      </div>
    </>
  );
};

export default NovoLeiloeiroModal;